import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { SikoAsmMainUiComponent } from '@siko/features/asm/components/asm-main-ui/asm-main-ui.component';
import { SikoCsAgentAuthService } from '@siko/features/asm/services/csagent-auth.service';
import { SikoOccCheckoutPaymentTypeAdapter } from '@siko/features/checkout/occ/adapters/occ-checkout-payment-type.adapter';
import { FooterOutletReplaceComponent } from '@siko/features/footer/components/footer-outlet-replace/footer-outlet-replace.component';
import { SikoHeaderOutletComponent } from '@siko/features/header/components/header-outlet/header-outlet.component';
import { StateWithSikoNews } from '@siko/features/news/store/siko-news-state';
import { SikoUserListService } from '@siko/features/organization/service/user-list.service';
import { SikoBlogDetailOutletComponent } from '@siko/features/outlets/components/blog/blog-detail/blog-detail-outlet.component';
import { SikoBlogListOutletComponent } from '@siko/features/outlets/components/blog/blog-list/blog-list-outlet.component';
import { SikoCheckoutOutletComponent } from '@siko/features/outlets/components/checkout-template/checkout-outlet.component';
import { SikoConfigurationTemplateComponent } from '@siko/features/outlets/components/configuration-template/configuration-template.component';
import { SikoMyAccountOutletComponent } from '@siko/features/outlets/components/my-account-wrapper/my-account-wrapper.component';
import { SikoNewsPageOutletComponent } from '@siko/features/outlets/components/news-page-template/news-page-template.component';
import { SikoOrderConfirmationOutletComponent } from '@siko/features/outlets/components/order-confirmation-wrapper/order-confirmation-outlet.component';
import { SikoProductGridOutletComponent } from '@siko/features/outlets/components/siko-product-grid/siko-product-grid-outlet.component';
import { SikoSearchOutletComponent } from '@siko/features/outlets/components/siko-search-outlet/siko-search-outlet.component';
import { SikoConfigurableRoutesService } from '@siko/routing/siko-configurable-routes.service';
import { SikoUtils } from '@siko/shared/utils/siko-utils';
import { SIKO_GLOBAL_TRANSLATIONS } from '@siko/translations/global/global.translations';
import { AsmConfig, CsAgentAuthService } from '@spartacus/asm/root';
import { translationChunksConfig } from '@spartacus/assets';
import { CartValidationGuard, MultiCartStatePersistenceService } from '@spartacus/cart/base/core';
import { CheckoutB2BAuthGuard } from '@spartacus/checkout/b2b/components';
import { CheckoutPaymentTypeAdapter } from '@spartacus/checkout/b2b/core';
import { CheckoutAuthGuard } from '@spartacus/checkout/base/components';
import { CheckoutDeliveryAddressEventListener } from '@spartacus/checkout/base/root';
import {
   AuthConfig,
   AuthHttpHeaderService,
   AuthRedirectService,
   AuthService,
   ConfigModule,
   CustomerCouponConnector,
   CustomLoginGuard,
   HttpErrorHandler,
   ProductSearchAdapter,
   ProtectedRoutesGuard,
   provideConfig,
   provideConfigFactory,
   RoutingService,
   SiteContextConfig,
   UserIdService,
} from '@spartacus/core';
import { UserListService } from '@spartacus/organization/administration/components';
import { CmsPageGuard, LogoutGuard, OutletPosition, PageLayoutComponent, provideOutlet } from '@spartacus/storefront';
import { UserAccountEventListener } from '@spartacus/user/account/root';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { sikoTranslationChunksConfig } from '../../public/i18n/translation-chunks-config';
import { SikoAuthHttpHeaderService } from './siko/auth/user-auth/services/auth-http-header.service';
import { SikoCheckoutDeliveryAddressEventListener, SikoUserAccountEventListener } from './siko/event-listeners';
import { SikoNewsActions } from './siko/features/news/store/actions';
import { SikoCartValidationGuard } from './siko/guards/cart-validation.guard';
import { ClearOrderStateGuard } from './siko/guards/clear-order-state.guard';
import { SikoLogoutGuard } from './siko/guards/logout.guard';
import { SikoB2cProtectedRoutesGuard } from './siko/guards/protected-routes.guard';
import { SikoCmsPageGuard } from './siko/guards/siko-cms-page.guard';
import { SikoAuthenticationErrorHandler } from './siko/interceptors/authentication-error.handler';
import { SikoBadGatewayHandler } from './siko/interceptors/bad-gateway.handler';
import { SikoHttpInterceptor } from './siko/interceptors/siko-http.interceptor';
import { SikoValidationErrorHandler } from './siko/interceptors/validation-error.handler';
import { SikoB2bProductDetailYouMightLikeRefecencesCarouselComponent } from '@siko/features/recommendation/components/product-detail-you-might-like-refecences-carousel/product-detail-you-might-like-refecences-carousel.component';
import { SikoB2bCustomerCouponConnector } from '@siko/features/my-account/customer-coupons/connectors/customer-coupon.connector';
import { SikoB2bCustomLoginGuard } from '@siko/guards/siko-custom-login.guard';
import { SikoB2bProductDetailLastSeenCarouselComponent } from '@siko/features/recommendation/components/product-detail-last-seen-carousel/last-seen-carousel.component';
import { SikoRateLimitInterceptor } from '@siko/common';
import { SikoB2BProductSearchAdapter } from './siko/adapter/product-search.adapter';
import { SikoB2bMultiCartStatePersistenceService } from '@siko/features/cart/services/multi-cart-state-persistence.service';

export const PRODUCT_DETAIL_EP = 'orgProducts/product/${productCode}?fields=FULL,showcasesCount';
export const USER_ANONYMOUS_UID = 'anonymous';

@NgModule({
   imports: [
      RouterModule.forChild([
         {
            path: 'my-account/sap-order/:sapOrderCode',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
         },
         {
            path: 'my-account/sap-quote/:sapQuoteId',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
         },
         {
            path: 'my-account/orders',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
            canDeactivate: [ClearOrderStateGuard],
         },
         {
            path: 'my-account/orders/:orderCode',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
            canDeactivate: [ClearOrderStateGuard],
         },
         {
            path: 'organization/units/:unitCode/documents/create-ticket/:code/:documentNumber',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
         },
         {
            path: 'organization/units/:unitCode/tickets',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
         },
         {
            path: 'organization/units/:unitCode/tickets/:ticketCode',
            component: PageLayoutComponent,
            canActivate: [CmsPageGuard],
         },
      ]),
      ConfigModule.withConfig({
         pagination: {
            addFirst: true,
            addLast: true,
            addDots: true,
         },
      }),
   ],
   providers: [
      provideOutlet({
         id: 'UpSelling',
         position: OutletPosition.BEFORE,
         component: SikoB2bProductDetailYouMightLikeRefecencesCarouselComponent,
      }),
      provideOutlet({
         id: 'UpSelling',
         position: OutletPosition.BEFORE,
         component: SikoB2bProductDetailLastSeenCarouselComponent,
      }),
      provideOutlet({
         id: 'SikoNewsPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoNewsPageOutletComponent,
      }),
      provideOutlet({
         id: 'AccountPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoMyAccountOutletComponent,
      }),
      provideOutlet({
         id: 'ProductGridPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoProductGridOutletComponent,
      }),
      provideOutlet({
         id: 'SearchResultsGridPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoSearchOutletComponent,
      }),
      provideOutlet({
         id: 'OrderConfirmationPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoOrderConfirmationOutletComponent,
      }),
      provideOutlet({
         id: 'MultiStepCheckoutSummaryPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoCheckoutOutletComponent,
      }),
      provideOutlet({
         id: 'SikoBlogPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoBlogListOutletComponent,
      }),
      provideOutlet({
         id: 'SikoBlogDetailPageTemplate',
         position: OutletPosition.REPLACE,
         component: SikoBlogDetailOutletComponent,
      }),
      provideOutlet({
         id: 'VariantConfigurationTemplate',
         position: OutletPosition.REPLACE,
         component: SikoConfigurationTemplateComponent,
      }),
      provideOutlet({
         id: 'cx-header',
         position: OutletPosition.REPLACE,
         component: SikoHeaderOutletComponent,
      }),
      provideOutlet({
         id: 'cx-footer',
         position: OutletPosition.REPLACE,
         component: FooterOutletReplaceComponent,
      }),
      provideConfig({
         backend: {
            occ: {
               endpoints: {
                  product: {
                     default: PRODUCT_DETAIL_EP,
                     details: PRODUCT_DETAIL_EP,
                     attributes: PRODUCT_DETAIL_EP,
                     price: PRODUCT_DETAIL_EP,
                     list: PRODUCT_DETAIL_EP,
                     stock: PRODUCT_DETAIL_EP,
                     variants: PRODUCT_DETAIL_EP,
                     list_item: PRODUCT_DETAIL_EP,
                  },
                  b2bUsers: '/users/${userId}/orgCustomers?fields=BASIC',
                  cart: 'users/${userId}/carts/current?fields=FULL',
                  paymentTypes: 'orgUsers/${userId}/paymenttypes',
                  storescounts: '/stores/storescounts',
                  // CWS overriden routes with prefix
                  deleteCart: 'b2b/users/${userId}/carts/${cartId}',
                  setDeliveryAddress: 'b2b/users/${userId}/carts/${cartId}/addresses/delivery',
                  userResetPassword: 'b2b/resetpassword',
                  cartVoucher: 'b2b/users/${userId}/carts/${cartId}/vouchers',
                  orgUnitsTree: '/b2b/users/${userId}/orgUnitsRootNodeTree',
                  stores: '/b2b/stores?fields=FULL',
                  removeEntries: 'b2b/users/${userId}/carts/${cartId}/entries/${entryNumber}',
                  userUpdatePassword: 'b2b/users/${userId}/password',
                  savedCart: 'b2b/users/${userId}/carts/${cartId}/savedcart',
                  restoreSavedCart: 'b2b/users/${userId}/carts/${cartId}/restoresavedcart',
                  cloneSavedCart: 'b2b/users/${userId}/carts/${cartId}/clonesavedcart',
                  savedCarts:
                     'b2b/users/${userId}/carts?savedCartsOnly=true&fields=carts(DEFAULT,entries(price(formattedValue,value),product(images(FULL),stock(FULL),unitData(FULL))),totalPrice(FULL),totalItems,saveTime,user,name,description)',
                  saveCart: 'b2b/users/${userId}/carts/${cartId}/save',
                  carts: 'b2b/users/${userId}/carts?fields=carts(DEFAULT,potentialProductPromotions,appliedProductPromotions,potentialOrderPromotions,appliedOrderPromotions,entries(totalPrice(formattedValue),product(images(FULL),stock(FULL)),basePrice(formattedValue,value),updateable),totalPrice(formattedValue),totalItems,totalPriceWithTax(formattedValue),totalDiscounts(value,formattedValue),subTotal(formattedValue),deliveryItemsQuantity,deliveryCost(formattedValue),totalTax(formattedValue, value),pickupItemsQuantity,net,appliedVouchers,productDiscounts(formattedValue),user,saveTime,name,description)',
                  userUpdateProfile: 'b2b/users/${userId}',
                  updateEntries: 'b2b/users/${userId}/carts/${cartId}/entries/${entryNumber}',
                  createDeliveryAddress: 'b2b/users/${userId}/carts/${cartId}/addresses/delivery',
                  removeDeliveryAddress: 'b2b/users/${userId}/carts/${cartId}/addresses/delivery',
                  orderDetail: 'b2b/users/${userId}/orders/${orderId}?fields=DEFAULT',
                  orderHistory: 'b2b/users/${userId}/orders?fields=FULL',
                  userRestoreToken: 'b2b/passwordRestoreToken',
                  productSearch: 'b2b/products/search?fields=BASIC',
                  productReferences:
                     'b2b/products/${productCode}/references?fields=FULL,references(target(numberOfReviews,averageRating,labels(FULL)))',
                  productReviews: 'b2b/products/${productCode}/reviews',
                  setDeliveryMode: 'b2b/users/${userId}/carts/${cartId}/deliverymode',
                  clearDeliveryMode: 'b2b/users/${userId}/carts/${cartId}/deliverymode',
                  createCart:
                     'b2b/users/${userId}/carts?fields=DEFAULT,potentialProductPromotions,appliedProductPromotions,potentialOrderPromotions,appliedOrderPromotions,entries(totalPrice(formattedValue),product(images(FULL),stock(FULL)),basePrice(formattedValue,value),updateable),totalPrice(formattedValue),totalItems,totalPriceWithTax(formattedValue),totalDiscounts(value,formattedValue),subTotal(formattedValue),deliveryItemsQuantity,deliveryCost(formattedValue),totalTax(formattedValue, value),pickupItemsQuantity,net,appliedVouchers,productDiscounts(formattedValue),user',
                  validate: 'b2b/users/${userId}/carts/${cartId}/validate?fields=DEFAULT',
                  claimCoupon: 'b2b/users/${userId}/customercoupons/${couponCode}/claim',
               },
            },
         },
         launch: {
            ASM: {
               outlet: 'cx-storefront',
               component: SikoAsmMainUiComponent,
            },
         },
      }),
      provideConfigFactory(() => {
         return {
            authentication: {
               tokenEndpoint: `/oauth/token`,
               loginUrl: '/oauth/authorize',
               revokeEndpoint: '/oauth/revoke',
               logoutUrl: '/logout',
               userinfoEndpoint: '/oauth/userinfo',
               sendAuthHeaderOnRevoke: false,
               useClientTokens: false,
               customLoginPage: {
                  csrfEndpoint: '/csrf?ngsw-bypass=true',
                  loginFormEndpoint: '/login',
               },
            },
         } as AuthConfig;
      }),
      provideConfigFactory(() => {
         const siteContext: SiteContextConfig | undefined = SikoUtils.getSiteContext(false);
         const baseSiteUid = siteContext ? siteContext.context?.baseSite : '';

         return {
            backend: {
               occ: {
                  endpoints: {
                     asmBindCart: `/occ/v2/${baseSiteUid}/b2b/bind-cart`,
                     // ... Potentially other dynamic endpoints
                  },
               },
            },
         };
      }),
      provideConfig({
         i18n: {
            chunks: translationChunksConfig,
         },
      }),
      provideConfig({
         i18n: {
            backend: {
               loader: (lng: string, ns: string) => import(`../../public/i18n/${lng}/${ns}.json`),
            },
            chunks: sikoTranslationChunksConfig,
         },
      }),
      provideConfig({
         i18n: {
            resources: SIKO_GLOBAL_TRANSLATIONS,
         },
      }),
      provideConfig({
         // TODO:Spartacus - The type of property 'asm: { agentSessionTimer?: { startingDelayInSeconds?: number; }; customerSearch?: { maxResults?: number; }; customerList?: { pageSize?: number; showAvatar?: boolean; columns?: { headerLocalizationKey: string; icon?: { symbol?: ICON_TYPE; captionLocalizationKey?: string; }; renderer?: (customer: User) => string; actionType?: CustomerListColumnActionType; }[]; }; userIdHttpHeader?: { enable?: boolean; }; }' changed to: 'asm: { agentSessionTimer?: { startingDelayInSeconds?: number; }; customerSearch?: { maxResults?: number; }; customerList?: { pageSize?: number; showAvatar?: boolean; columns?: { headerLocalizationKey: string; icon?: { symbol?: ICON_TYPE; captionLocalizationKey?: string; }; renderer?: (customer: User) => string; actionType?: CustomerListColumnActionType; }[]; }; asmSessionSupport?: { enabled?: boolean; }; userIdHttpHeader?: { enable?: boolean; }; createCustomer?: { enable?: boolean; }; }'
         asm: {
            userIdHttpHeader: {
               enable: true,
            },
         },
      } as AsmConfig),
      {
         provide: CustomerCouponConnector,
         useClass: SikoB2bCustomerCouponConnector,
      },
      {
         provide: AuthHttpHeaderService,
         useExisting: SikoAuthHttpHeaderService,
      },
      {
         provide: CmsPageGuard,
         useExisting: SikoCmsPageGuard,
      },
      {
         provide: CustomLoginGuard,
         useExisting: SikoB2bCustomLoginGuard,
      },
      {
         provide: ProtectedRoutesGuard,
         useExisting: SikoB2cProtectedRoutesGuard,
      },
      {
         provide: HttpErrorHandler,
         useExisting: SikoValidationErrorHandler,
         multi: true,
      },
      {
         provide: HttpErrorHandler,
         useExisting: SikoAuthenticationErrorHandler,
         multi: true,
      },
      {
         provide: HttpErrorHandler,
         useExisting: SikoBadGatewayHandler,
         multi: true,
      },
      {
         provide: CartValidationGuard,
         useClass: SikoCartValidationGuard,
      },
      {
         provide: LogoutGuard,
         useClass: SikoLogoutGuard,
      },
      {
         provide: UserAccountEventListener,
         useClass: SikoUserAccountEventListener,
      },
      {
         provide: CsAgentAuthService,
         useClass: SikoCsAgentAuthService,
      },
      {
         provide: UserListService,
         useExisting: SikoUserListService,
      },
      {
         provide: CheckoutDeliveryAddressEventListener,
         useClass: SikoCheckoutDeliveryAddressEventListener,
      },
      {
         provide: CheckoutAuthGuard,
         useExisting: CheckoutB2BAuthGuard,
      },
      {
         provide: CheckoutPaymentTypeAdapter,
         useClass: SikoOccCheckoutPaymentTypeAdapter,
      },
      {
         provide: MultiCartStatePersistenceService,
         useClass: SikoB2bMultiCartStatePersistenceService
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: SikoHttpInterceptor,
         multi: true,
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: SikoRateLimitInterceptor,
         multi: true,
      },
      {
         provide: ProductSearchAdapter,
         useClass: SikoB2BProductSearchAdapter,
      },
   ],
})
export class SikoB2bCoreModule {
   constructor(
      readonly authRedirectService: AuthRedirectService,
      readonly store: Store<StateWithSikoNews>,
      readonly authService: AuthService,
      readonly configurableRoutesService: SikoConfigurableRoutesService,
      readonly userIdService: UserIdService,
      readonly routingService: RoutingService
   ) {
      combineLatest([
         this.userIdService.getUserId().pipe(distinctUntilChanged()),
         this.authService.isUserLoggedIn().pipe(distinctUntilChanged()),
      ])
         .pipe(
            filter(([userId, isLoggedIn]) => isLoggedIn && userId !== USER_ANONYMOUS_UID),
            map(() => true)
         )
         .subscribe(() => {
            this.store.dispatch(new SikoNewsActions.LoadUnseenNewsCount());
         });

      this.authService.logoutInProgress$.pipe(distinctUntilChanged()).subscribe((logoutInProgress: boolean) => {
         if (logoutInProgress) {
            this.authRedirectService.setRedirectUrl('');
         }
      });

      this.configurableRoutesService.initOverrideRoutes();
   }
}
